/*!
 * png-encoder.js
 * Streaming PNG encoder (RGBA, 8-bit) with real DPI metadata (pHYs chunk).
 *
 * Why this exists: a 22in x 120in sheet at 300 DPI is 6600 x 36000 px
 * (237 megapixels). That exceeds the maximum canvas area of Firefox and
 * Safari, so canvas.toBlob() on a single full-size canvas fails silently.
 * This encoder receives the image in horizontal strips (rows) and builds
 * a valid PNG without ever allocating the full bitmap, and it embeds
 * 300 DPI in the file itself (browsers' toBlob() writes no DPI at all).
 *
 * Compression: uses the browser-native CompressionStream('deflate')
 * (zlib/RFC1950) when available, and falls back to pako (vendored) on
 * older browsers. A custom deflator factory can also be injected via
 * opts.deflator (used by tests).
 *
 * Usage:
 *   const enc = PngEncoder.create({ width, height, dpi: 300 });
 *   await enc.addRows(imageData.data, numRows); // repeat until all rows sent
 *   const blob = await enc.finish();            // Blob('image/png')
 */
(function (root, factory) {
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = factory();
  } else {
    root.PngEncoder = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ---------------------------------------------------------------- CRC-32
  var CRC_TABLE = (function () {
    var table = new Uint32Array(256);
    for (var n = 0; n < 256; n++) {
      var c = n;
      for (var k = 0; k < 8; k++) {
        c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
      }
      table[n] = c >>> 0;
    }
    return table;
  })();

  // CRC over a list of byte arrays (chunk type + chunk data).
  function crc32(arrays) {
    var c = 0xffffffff;
    for (var i = 0; i < arrays.length; i++) {
      var a = arrays[i];
      for (var j = 0; j < a.length; j++) {
        c = CRC_TABLE[(c ^ a[j]) & 0xff] ^ (c >>> 8);
      }
    }
    return (c ^ 0xffffffff) >>> 0;
  }

  function u32be(v) {
    return new Uint8Array([(v >>> 24) & 0xff, (v >>> 16) & 0xff, (v >>> 8) & 0xff, v & 0xff]);
  }

  function asciiBytes(s) {
    var out = new Uint8Array(s.length);
    for (var i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0x7f;
    return out;
  }

  // length(4) + type(4) + data + crc(4)
  function makeChunk(type, data) {
    if (!(data instanceof Uint8Array)) data = new Uint8Array(data);
    var t = asciiBytes(type);
    var out = new Uint8Array(12 + data.length);
    out.set(u32be(data.length), 0);
    out.set(t, 4);
    out.set(data, 8);
    out.set(u32be(crc32([t, data])), 8 + data.length);
    return out;
  }

  // ------------------------------------------------------------- deflators
  // A deflator produces a zlib stream (RFC1950), which is what PNG IDAT
  // requires. Compressed chunks are delivered to onChunk AS THEY ARE PRODUCED
  // (not accumulated), so the caller can wrap them into IDAT chunks
  // incrementally: memory holds one copy of the compressed data and the CRC
  // work is amortized across the export instead of freezing the UI at the end.
  // Interface: push(Uint8Array) -> Promise, finish() -> Promise (resolves once
  // every chunk has been delivered).

  function createCompressionStreamDeflator(onChunk) {
    var cs = new CompressionStream('deflate'); // 'deflate' = zlib format
    var writer = cs.writable.getWriter();
    var reader = cs.readable.getReader();
    var pump = (function loop() {
      return reader.read().then(function (res) {
        if (res.done) return;
        onChunk(res.value);
        return loop();
      });
    })();
    return {
      name: 'CompressionStream',
      push: function (u8) {
        return writer.ready.then(function () { return writer.write(u8); });
      },
      finish: function () {
        return writer.close().then(function () { return pump; });
      }
    };
  }

  function createPakoDeflator(pakoLib, onChunk) {
    var d = new pakoLib.Deflate({ level: 6 });
    d.onData = onChunk;
    // pako reports zlib failures ONLY through onEnd(status); record them so
    // the d.err checks below actually fire instead of emitting a corrupt PNG
    d.onEnd = function (status) {
      if (status !== 0 && !d.err) {
        d.err = status;
        d.msg = d.msg || ('zlib status ' + status);
      }
    };
    return {
      name: 'pako',
      push: function (u8) {
        d.push(u8, false);
        return d.err ? Promise.reject(new Error('pako deflate error: ' + d.msg)) : Promise.resolve();
      },
      finish: function () {
        d.push(new Uint8Array(0), true); // Z_FINISH
        return d.err ? Promise.reject(new Error('pako deflate error: ' + d.msg)) : Promise.resolve();
      }
    };
  }

  // ----------------------------------------------------------------- main
  function create(opts) {
    var width = opts.width | 0;
    var height = opts.height | 0;
    var dpi = opts.dpi || 300;
    if (width < 1 || height < 1) {
      throw new Error('PngEncoder: invalid dimensions ' + opts.width + 'x' + opts.height);
    }

    // compressed chunks become IDAT chunks the moment they arrive
    var idatParts = [];
    function onZChunk(c) {
      idatParts.push(makeChunk('IDAT', c));
    }

    var deflator = null;
    if (typeof opts.deflator === 'function') {
      deflator = opts.deflator(onZChunk); // injectable factory (used by tests)
    } else if (typeof CompressionStream !== 'undefined') {
      deflator = createCompressionStreamDeflator(onZChunk);
    } else if (typeof pako !== 'undefined') {
      deflator = createPakoDeflator(pako, onZChunk);
    }
    if (!deflator) {
      throw new Error('PngEncoder: no deflate implementation available (need CompressionStream or pako)');
    }

    var bytesPerRow = width * 4;
    var rowsWritten = 0;
    var finished = false;

    return {
      deflatorName: deflator.name,

      // rgba: Uint8Array or Uint8ClampedArray (e.g. ImageData.data) with
      // exactly rows * width * 4 bytes. Rows are sent top to bottom.
      addRows: function (rgba, rows) {
        rows = rows | 0;
        if (finished) return Promise.reject(new Error('PngEncoder: already finished'));
        if (rows < 1) return Promise.reject(new Error('PngEncoder: rows must be >= 1'));
        if (rgba.length < rows * bytesPerRow) {
          return Promise.reject(new Error('PngEncoder: buffer too small for ' + rows + ' rows'));
        }
        if (rowsWritten + rows > height) {
          return Promise.reject(new Error('PngEncoder: more rows than declared height'));
        }
        // Prepend the PNG filter byte (0 = None) to every scanline.
        var filtered = new Uint8Array(rows * (bytesPerRow + 1));
        var o = 0;
        for (var r = 0; r < rows; r++) {
          filtered[o++] = 0;
          filtered.set(rgba.subarray(r * bytesPerRow, (r + 1) * bytesPerRow), o);
          o += bytesPerRow;
        }
        rowsWritten += rows;
        return deflator.push(filtered);
      },

      finish: function () {
        if (finished) return Promise.reject(new Error('PngEncoder: already finished'));
        finished = true;
        if (rowsWritten !== height) {
          return Promise.reject(new Error('PngEncoder: wrote ' + rowsWritten + ' rows, expected ' + height));
        }
        return deflator.finish().then(function () {
          var parts = [];
          parts.push(new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]));

          var ihdr = new Uint8Array(13);
          ihdr.set(u32be(width), 0);
          ihdr.set(u32be(height), 4);
          ihdr[8] = 8;  // bit depth
          ihdr[9] = 6;  // color type: RGBA (truecolor with alpha)
          ihdr[10] = 0; // compression: deflate
          ihdr[11] = 0; // filter method 0
          ihdr[12] = 0; // no interlace
          parts.push(makeChunk('IHDR', ihdr));

          parts.push(makeChunk('sRGB', new Uint8Array([0]))); // perceptual intent

          // pHYs: pixels per meter. 300 dpi -> 300 / 0.0254 = 11811 ppm.
          var ppm = Math.round(dpi / 0.0254);
          var phys = new Uint8Array(9);
          phys.set(u32be(ppm), 0);
          phys.set(u32be(ppm), 4);
          phys[8] = 1; // unit: meter
          parts.push(makeChunk('pHYs', phys));

          var software = opts.software || 'DTF Transfers Now Gang Sheet Builder';
          var softwareText = 'Software' + String.fromCharCode(0) + software;
          parts.push(makeChunk('tEXt', asciiBytes(softwareText)));

          for (var i = 0; i < idatParts.length; i++) {
            parts.push(idatParts[i]);
          }
          idatParts = null;
          parts.push(makeChunk('IEND', new Uint8Array(0)));
          return new Blob(parts, { type: 'image/png' });
        });
      },

      rowsWritten: function () { return rowsWritten; }
    };
  }

  return {
    create: create,
    // exposed for unit tests
    _crc32: crc32,
    _makeChunk: makeChunk,
    _createPakoDeflator: createPakoDeflator
  };
});
